import { useCallback, useState } from "react";
import { EncryptedS3CredentialService } from "@/services/s3/EncryptedS3CredentialService";
import {
  type StorageService,
  StorageServiceFactory,
} from "@/services/storage/StorageServiceFactory";

interface UseS3StorageOptions {
  craftspaceId: string;
}

export const useS3Storage = ({ craftspaceId }: UseS3StorageOptions) => {
  const [storageService, setStorageService] = useState<StorageService | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);

  const initializeStorage = useCallback(async () => {
    setIsLoading(true);
    try {
      const service = await StorageServiceFactory.createStorageService(craftspaceId);
      setStorageService(service);
      setNeedsPassword(false);
      return service;
    } catch (error) {
      console.error("Storage service initialization failed:", error);
      setNeedsPassword(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [craftspaceId]);

  const uploadFile = useCallback(
    async (file: File, path: string): Promise<string> => {
      if (!storageService) {
        // Try to initialize storage with stored credentials
        try {
          await initializeStorage();
        } catch (error) {
          console.error("Storage initialization failed:", error);
          throw error;
        }
      }

      if (!storageService) {
        console.error("Storage service still not available after initialization attempt");
        throw new Error("Storage service not available");
      }

      try {
        return await storageService.uploadFile(file, path);
      } catch (error) {
        console.error("Storage service uploadFile failed:", error);
        // If credentials expired, clear them and reinitialize
        if (
          error instanceof Error &&
          (error.message.includes("InvalidAccessKeyId") ||
            error.message.includes("SignatureDoesNotMatch"))
        ) {
          EncryptedS3CredentialService.removeCredentials(craftspaceId);
          setStorageService(null);
          setNeedsPassword(false);
          throw new Error(
            "S3 credentials expired. Please reconfigure your S3 connection in craftspace settings.",
          );
        }
        throw error;
      }
    },
    [storageService, initializeStorage, craftspaceId],
  );

  const deleteFile = useCallback(
    async (path: string): Promise<void> => {
      if (!storageService) {
        await initializeStorage();
      }

      if (!storageService) {
        throw new Error("Storage service not available");
      }

      try {
        await storageService.deleteFile(path);
      } catch (error) {
        if (
          error instanceof Error &&
          (error.message.includes("InvalidAccessKeyId") ||
            error.message.includes("SignatureDoesNotMatch"))
        ) {
          EncryptedS3CredentialService.removeCredentials(craftspaceId);
          setStorageService(null);
          setNeedsPassword(false);
          throw new Error(
            "S3 credentials expired. Please reconfigure your S3 connection in craftspace settings.",
          );
        }
        throw error;
      }
    },
    [storageService, initializeStorage, craftspaceId],
  );

  const getFileUrl = useCallback(
    async (path: string): Promise<string> => {
      if (!storageService) {
        await initializeStorage();
      }

      if (!storageService) {
        throw new Error("Storage service not available");
      }

      try {
        return await storageService.getFileUrl(path);
      } catch (error) {
        if (
          error instanceof Error &&
          (error.message.includes("InvalidAccessKeyId") ||
            error.message.includes("SignatureDoesNotMatch"))
        ) {
          EncryptedS3CredentialService.removeCredentials(craftspaceId);
          setStorageService(null);
          setNeedsPassword(false);
          throw new Error(
            "S3 credentials expired. Please reconfigure your S3 connection in craftspace settings.",
          );
        }
        throw error;
      }
    },
    [storageService, initializeStorage, craftspaceId],
  );

  const generateUploadUrl = useCallback(
    async (path: string, contentType: string): Promise<string> => {
      if (!storageService) {
        await initializeStorage();
      }

      if (!storageService) {
        throw new Error("Storage service not available");
      }

      try {
        return await storageService.generateUploadUrl(path, contentType);
      } catch (error) {
        if (
          error instanceof Error &&
          (error.message.includes("InvalidAccessKeyId") ||
            error.message.includes("SignatureDoesNotMatch"))
        ) {
          EncryptedS3CredentialService.removeCredentials(craftspaceId);
          setStorageService(null);
          setNeedsPassword(false);
          throw new Error(
            "S3 credentials expired. Please reconfigure your S3 connection in craftspace settings.",
          );
        }
        throw error;
      }
    },
    [storageService, initializeStorage, craftspaceId],
  );

  const testConnection = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!storageService) {
      await initializeStorage();
    }

    if (!storageService) {
      return { success: false, error: "Storage service not available" };
    }

    try {
      return await storageService.testConnection();
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }, [storageService, initializeStorage]);

  const clearCredentials = useCallback(() => {
    EncryptedS3CredentialService.removeCredentials(craftspaceId);
    setStorageService(null);
    setNeedsPassword(false);
  }, [craftspaceId]);

  const getStorageProvider = useCallback(async () => {
    return await StorageServiceFactory.getCraftspaceStorageProvider(craftspaceId);
  }, [craftspaceId]);

  const hasStoredCredentials = useCallback(async () => {
    return await StorageServiceFactory.hasStoredCredentials(craftspaceId);
  }, [craftspaceId]);

  const getCredentialAge = useCallback(async () => {
    return await StorageServiceFactory.getCredentialAge(craftspaceId);
  }, [craftspaceId]);

  return {
    storageService,
    isLoading,
    needsPassword,
    uploadFile,
    deleteFile,
    getFileUrl,
    generateUploadUrl,
    testConnection,
    initializeStorage,
    clearCredentials,
    getStorageProvider,
    hasStoredCredentials,
    getCredentialAge,
  };
};
